import { useEffect, useState } from "react";
import { ImageOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { PosPlain } from "./PosPlain";
import { ManagerNote, ReviewActions } from "./ReviewActions";
import { RecordAudit } from "./RecordAudit";
import { RecordDelete } from "./RecordDelete";
import { coMessage, flagLabel, fmt, fmtDateTime, fmtSar, labelOf, PHOTO_STATUS_KEY } from "./coUtils";

const P = OPS.co.panel;
const title = "text-xs font-bold text-[color:var(--ink-muted)]";

function RecordFields({ rec, scope }) {
    const { t, tn, lang } = useI18n();
    const L = scope?.labels || {};
    const branch = (scope?.branches || []).find((b) => b.id === rec.branch_id);
    const yes = t("co_rec_yes");
    const customer = rec.phone_kind === "mobile" ? (rec.customer_phone || rec.phone_masked) : t(`co_phone_kind_${rec.phone_kind}`);
    const rows = [
        ["invoice", rec.invoice_no, true],
        ["value", `${fmtSar(rec.value_sar, lang, { halalas: false })} ${lang === "ar" ? "ر.س" : "SAR"}`],
        ["business_date", rec.business_date, true],
        ["time", fmtDateTime(rec.occurred_at, lang)],
        ["branch", branch ? tn(branch) : ""],
        ["category", labelOf(L.categories, rec.category, lang)],
        ["reason", rec.reason_text],
        ["outcome", labelOf(L.outcomes, rec.outcome, lang)],
        ["outcome_note", rec.outcome_note],
        ["reissued", rec.reissued_invoice_no, true],
        ["prepared", rec.prepared == null ? "" : (rec.prepared ? yes : t("co_rec_no"))],
        ["source", labelOf(L.sources, rec.source, lang)],
        ["customer", customer, rec.phone_kind === "mobile"],
        ["recorded_by", rec.cashier_name || rec.created_by_name],
        ["created_at", fmtDateTime(rec.created_at, lang)],
        ["updated", rec.updated_at ? `${rec.updated_by_name || ""} · ${fmtDateTime(rec.updated_at, lang)}` : ""],
        ["origin", rec.origin ? t(`co_rec_origin_${rec.origin}`) : ""],
        ["duplicate", rec.duplicate_of && !rec.duplicate_dismissed ? yes : ""],
        ["photo", t(PHOTO_STATUS_KEY[rec.photo_status] || "co_photo_failed")],
    ].filter(([, v]) => v !== null && v !== undefined && v !== "");
    return (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5">
            {rows.map(([k, v, ltr]) => (
                <div key={k} data-testid={P.field(k)} className="min-w-0">
                    <dt className="text-[11px] text-[color:var(--ink-faint)]">{t(`co_rec_field_${k}`)}</dt>
                    <dd className="break-words text-sm font-semibold text-[color:var(--ink)]">{ltr ? <span dir="ltr">{v}</span> : v}</dd>
                </div>
            ))}
            {rec.backdated ? <div className="col-span-2 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">{t("co_badge_backdated")}</div> : null}
        </dl>
    );
}

const missingBox = "flex items-center gap-1.5 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-soft)] px-3 py-4 text-xs font-semibold text-[color:var(--ink-muted)]";

/** co_admin / master only (`photo_diag`, GET /co/records/{id}): why the photo is where it is. */
function PhotoDiag({ diag, t }) {
    if (!diag) return null;
    const dash = "—";
    return (
        <p data-testid={P.photoDiag} data-status={diag.status} dir="auto"
           className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">
            {fmt(t("co_rec_photo_diag"), {
                status: diag.status, attempts: diag.attempts, rejected: diag.rejected_count,
                rejection: diag.last_rejection || dash, error: diag.last_client_error || dash,
            })}
        </p>
    );
}

function PhotoBlock({ rec }) {
    const { t } = useI18n();
    const [url, setUrl] = useState(null);
    const [err, setErr] = useState(false);
    // the photo exists but could not be shown (an expired link, storage down): not «no photo»
    const [broken, setBroken] = useState(false);
    useEffect(() => {
        setUrl(null); setErr(false); setBroken(false);
        if (!rec.has_photo) return undefined;
        let alive = true;
        api.get(`/co/records/${rec.id}/photo`, { params: { json: 1 } })
            .then((r) => { if (alive) setUrl(r.data.url); })
            .catch((e) => { if (alive) (e?.response?.status === 404 ? setErr : setBroken)(true); });
        return () => { alive = false; };
    }, [rec.id, rec.has_photo]);
    return (
        <section className="space-y-2">
            <h4 className={title}>{t("co_rec_photo_title")}</h4>
            {!rec.has_photo || err ? (
                <p data-testid={P.photoMissing} data-status={rec.photo_status} className={missingBox}>
                    <ImageOff className="h-4 w-4" /><span>{t("co_rec_photo_missing")}</span>
                </p>
            ) : broken ? (
                <p data-testid={P.photoBroken} role="alert" className={missingBox}>
                    <ImageOff className="h-4 w-4" /><span>{t("co_photo_view_failed")}</span>
                </p>
            ) : url ? (
                <a href={url} target="_blank" rel="noreferrer">
                    <img data-testid={P.photo} src={url} alt="" onError={() => setBroken(true)}
                         className="max-h-80 w-full rounded-[var(--radius-md)] bg-[color:var(--bg-surface-2)] object-contain" />
                </a>
            ) : <InlineLoader />}
            <PhotoDiag diag={rec.photo_diag} t={t} />
        </section>
    );
}

/** The record panel: every field, the photo (signed URL), the `pos` block in plain words,
 *  `manager_note`, the review action and the record's audit trail. */
export function RecordSheet({ recordId, scope, onClose, onChanged }) {
    const { t, lang } = useI18n();
    const [rec, setRec] = useState(null);
    const [auditKey, setAuditKey] = useState(0);

    useEffect(() => {
        setRec(null);
        if (!recordId) return undefined;
        let alive = true;
        api.get(`/co/records/${recordId}`)
            .then((r) => { if (alive) setRec(r.data); })
            .catch((e) => { if (alive) { toast.error(coMessage(e, lang) || t("co_offline_error")); onClose(); } });
        return () => { alive = false; };
    }, [recordId]); // eslint-disable-line react-hooks/exhaustive-deps

    // a review / note answer carries no photo_diag (the detail read does): keep the one we have
    const saved = (r) => {
        setRec((cur) => ({ ...r, photo_diag: r.photo_diag || cur?.photo_diag }));
        setAuditKey((k) => k + 1); onChanged && onChanged();
    };
    const flags = rec?.review?.flags || [];

    return (
        <Sheet open={!!recordId} onOpenChange={(o) => { if (!o) onClose(); }}>
            <SheetContent side="right" data-testid={P.sheet} className="w-full overflow-y-auto bg-[color:var(--bg-surface)] p-0 sm:max-w-lg">
                <SheetHeader className="sticky top-0 z-10 border-b border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] px-4 py-3 pr-12 text-start sm:text-start">
                    <SheetTitle className="text-base font-extrabold text-[color:var(--ink)]">
                        <span>{t("co_rec_panel_title")}</span>{rec ? <span> · <span dir="ltr">#{rec.invoice_no}</span></span> : null}
                    </SheetTitle>
                    <SheetDescription className="sr-only">{t("co_rec_panel_title")}</SheetDescription>
                </SheetHeader>
                {!rec ? <InlineLoader /> : (
                    <div className="space-y-5 p-4">
                        <RecordFields rec={rec} scope={scope} />
                        <section className="space-y-1.5 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] p-3">
                            <h4 className={title}>{t("co_rec_pos_title")}</h4>
                            <PosPlain pos={rec.pos} labels={scope?.labels} t={t} lang={lang} testId={P.pos} />
                        </section>
                        {flags.length ? (
                            <section data-testid={P.flags} className="space-y-1.5">
                                <h4 className={title}>{t("co_rec_flags")}</h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {flags.map((f) => (
                                        <span key={f} data-flag={f} className="rounded-full bg-[color:var(--warning-soft)] px-2.5 py-1 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                                            {flagLabel(t, f)}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        ) : null}
                        <PhotoBlock rec={rec} />
                        <ManagerNote key={`n-${rec.id}-${auditKey}`} rec={rec} scope={scope} onSaved={saved} />
                        <ReviewActions key={`r-${rec.id}-${auditKey}`} rec={rec} scope={scope} onSaved={saved} />
                        <RecordAudit recordId={rec.id} reloadKey={auditKey} labels={scope?.labels} />
                        <RecordDelete rec={rec} scope={scope} onDeleted={() => { onChanged && onChanged(); onClose(); }} />
                        <button type="button" data-testid={P.close} onClick={onClose}
                                className="h-11 w-full rounded-full border border-[color:var(--border-soft)] text-sm font-bold text-[color:var(--ink)] transition-[background-color] duration-150 hover:bg-[color:var(--bg-surface-2)]">
                            {t("co_rec_close")}
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
